import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';
import { ACCOUNT_HEADER } from '@/lib/activeAccount';

export interface Invitation {
  id: string;
  organizationId: string;
  email: string;
  // No `token`: the API deliberately withholds it. It is the capability to
  // accept the invitation, so listing invitations must not hand out the
  // credential the email is delivering.
  invitedBy: string;
  invitedByName?: string;
  expiresAt: string;
  acceptedAt?: string;
  teamId: string;
  contextId?: string;
  createdAt: string;
  updatedAt: string;
  isExpired: boolean;
  isValid: boolean;
}

interface InvitationListResponse {
  invitations: Invitation[];
}

interface CreateInvitationRequest {
  email: string;
  teamId: string;
  contextId?: string;
}

interface ResendInvitationRequest {
  expirationDays?: number;
}

interface AcceptInvitationRequest {
  token: string;
  firstName: string;
  lastName: string;
  password: string;
}

interface AcceptInvitationResponse {
  message: string;
  organizationId: string;
  organizationName: string;
}

interface InvitationInfoResponse {
  email: string;
  organizationName: string;
  inviterName?: string;
  expiresAt: string;
  isExpired: boolean;
  isAccepted: boolean;
  /** The invited address already belongs to an account, so offer signing in
   *  rather than a signup the backend can only reject. Deliberately a bare
   *  boolean — which methods that account can use is not disclosed. */
  accountExists: boolean;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
}

const backendApi = config.backendApi;

const fetchInvitations = async (): Promise<Invitation[]> => {
  const response = await axios.get<InvitationListResponse>(
    `${backendApi}/api/invitations`,
    { withCredentials: true }
  );
  return response.data.invitations;
};

const createInvitation = async (request: CreateInvitationRequest): Promise<Invitation> => {
  const response = await axios.post<Invitation>(
    `${backendApi}/api/invitations`,
    request,
    { withCredentials: true }
  );
  return response.data;
};

const resendInvitation = async (id: string, request: ResendInvitationRequest): Promise<Invitation> => {
  const response = await axios.post<Invitation>(
    `${backendApi}/api/invitations/${id}/resend`,
    request,
    { withCredentials: true }
  );
  return response.data;
};

const deleteInvitation = async (id: string): Promise<void> => {
  await axios.delete(`${backendApi}/api/invitations/${id}`, { withCredentials: true });
};

export function useInvitations(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Invitation[]>({
    queryKey: ['invitations', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch invitations.");
      }
      return fetchInvitations();
    },
    enabled: !!organizationId && contextReady,
  });
}

export function useCreateInvitation() {
  const queryClient = useQueryClient();

  return useMutation<Invitation, AxiosError<ApiErrorResponse>, CreateInvitationRequest>({
    mutationFn: createInvitation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation<Invitation, AxiosError<ApiErrorResponse>, { id: string; request: ResendInvitationRequest }>({
    mutationFn: ({ id, request }) => resendInvitation(id, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

export function useDeleteInvitation() {
  const queryClient = useQueryClient();

  return useMutation<undefined, AxiosError<ApiErrorResponse>, string>({
    mutationFn: async (id) => {
      await deleteInvitation(id);
      return undefined;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

// Public API - no authentication required
const getInvitationInfo = async (token: string): Promise<InvitationInfoResponse> => {
  const response = await axios.get<InvitationInfoResponse>(
    `${backendApi}/api/auth/invitation-info/${token}`
  );
  return response.data;
};

const acceptInvitation = async (request: AcceptInvitationRequest): Promise<AcceptInvitationResponse> => {
  const response = await axios.post<AcceptInvitationResponse>(
    `${backendApi}/api/auth/accept-invitation`,
    request
  );
  return response.data;
};

export function useInvitationInfo(token: string | undefined) {
  return useQuery<InvitationInfoResponse, AxiosError<ApiErrorResponse>>({
    queryKey: ['invitationInfo', token],
    queryFn: () => {
      if (!token) {
        throw new Error('Token is required');
      }
      return getInvitationInfo(token);
    },
    enabled: !!token,
    retry: false, // Don't retry on 404 or expired invitations
    staleTime: Infinity, // Invitation info doesn't change
  });
}

export function useAcceptInvitation() {
  return useMutation<AcceptInvitationResponse, AxiosError<ApiErrorResponse>, AcceptInvitationRequest>({
    mutationFn: acceptInvitation,
  });
}

interface AcceptInvitationAuthenticatedResponse {
  message: string;
  organizationId: string;
  organizationName: string;
}

// Accept an invitation as the already-signed-in user (joins the existing
// account to the invitation's team rather than creating a new account).
interface AcceptAuthenticatedParams {
  token: string;
  /**
   * The signed-in account that joins, when it isn't the tab's own
   * (docs/MultiAccountSessions.md §7). The browser must hold it.
   */
  account?: string;
}

/**
 * The headers that accept as `account` rather than as the tab.
 *
 * Without the tab's context, too: that context is an organization of the tab's account, and
 * the joining account is not in it — it is joining one — so `AuthLayer` would refuse the
 * request as a reach into an organization it doesn't belong to. Accepting needs no context.
 * `false` is axios's "don't send this one", over the header every request carries.
 */
function acceptAsHeaders(account: string): Record<string, string | false> {
  return { [ACCOUNT_HEADER]: account, 'X-Active-Context-Id': false };
}

const acceptInvitationAuthenticated = async ({
  token,
  account,
}: AcceptAuthenticatedParams): Promise<AcceptInvitationAuthenticatedResponse> => {
  const response = await axios.post<AcceptInvitationAuthenticatedResponse>(
    `${backendApi}/api/invitations/${token}/accept`,
    {},
    { withCredentials: true, ...(account ? { headers: acceptAsHeaders(account) } : {}) },
  );
  return response.data;
};

export function useAcceptInvitationAuthenticated() {
  const queryClient = useQueryClient();

  return useMutation<AcceptInvitationAuthenticatedResponse, AxiosError<ApiErrorResponse>, AcceptAuthenticatedParams>({
    mutationFn: acceptInvitationAuthenticated,
    onSuccess: () => {
      // The user's org/team membership changed — refresh identity + lists.
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      void queryClient.invalidateQueries({ queryKey: ['availableContexts'] });
    },
  });
}
